import map from 'lodash/map'
import { generateId } from '@ztechapps/common.utils'
import type { User } from '@ztechapps/common.contract'
import type { TemplateTask } from './TaskTemplateStatusActionEditor'

/**
 * Trims selected users down to the canonical stored shape `{ _id, profile }`,
 * dropping react-select cruft (`value`/`label`) and unneeded relations
 * (`company`/`status`). `_id` is the field the backend reads when creating
 * tasks; `profile` is kept only so the editor chips and preview avatars render
 * without a re-fetch on reopen.
 */
export const normalizeMembers = (members?: User[]): User[] =>
  map(
    members,
    (member) =>
      ({
        _id: member?._id,
        profile: member?.profile
          ? { _id: member.profile._id, name: member.profile.name, picture: member.profile.picture }
          : undefined,
      }) as User
  )

/**
 * Ensures every task carries a transient `_id` used only as a stable drag-sort
 * key (FLIP correlation). Parsed-from-JSON tasks have no `_id`, so one is
 * generated. The `_id` is never persisted — see toPersistableTasks.
 */
export const withDragKeys = (tasks: TemplateTask[]): TemplateTask[] =>
  map(tasks, (task) => (task?._id ? task : { ...task, _id: generateId() }))

/**
 * Strips the transient drag-sort `_id` so the persisted Template.value JSON
 * keeps the canonical shape `{ name, members?, statusActions? }`. Without this
 * the client-only key would leak to the backend as a Task `_id`.
 */
export const toPersistableTasks = (tasks: TemplateTask[]): Omit<TemplateTask, '_id'>[] =>
  map(tasks, ({ _id: _dragKey, ...task }) => task)
